/**
 * Helpers that allow React to pull props from jQuery plugins.
 */

import { ComponentType, JQueryPlugin, type BlockComponent } from 'types';

import { resizableProps as embeddedHtmlProps } from '../react/component-embedded-html';
import { resizableProps as heroProps } from 'lib/components/block-component-renderers/react/component-hero';
import { resizableProps as measureProps } from 'lib/components/block-component-renderers/react/component-measure/component-measure';
import { resizableProps as inSituProps } from 'lib/components/block-component-renderers/react/component-in-situ-visualization';
import { resizableProps as mapProps } from 'lib/components/block-component-renderers/react/component-map';
import { resizableProps as tableProps } from 'lib/components/block-component-renderers/componentSocrataVisualizationTable';
import { resizableProps as vizCanProps } from 'lib/components/block-component-renderers/react/component-viz-canvas/component-viz-canvas';

interface ResizableProps {
  resizeSupported: boolean;
  resizeOptions?: {
    minHeight?: number;
  };
  defaultHeight?: number;
}

export const getResizableComponentProps = (
  type: JQueryPlugin,
  componentData: BlockComponent,
): ResizableProps => {
  switch (type) {
    case JQueryPlugin.EMBEDDED_HTML:
      return embeddedHtmlProps();
    case JQueryPlugin.HERO:
      return heroProps(componentData);
    case JQueryPlugin.INSITU:
      return inSituProps(componentData);
    case JQueryPlugin.MAP:
      return mapProps();
    case JQueryPlugin.MEASURE:
      return measureProps(componentData);
    case JQueryPlugin.TABLE:
      return tableProps();
    case JQueryPlugin.VIZ_CANVAS:
      return vizCanProps();
    default:
      return {
        resizeSupported: false,
      };
  }
};

export const getComponentRenderer = (type: ComponentType): JQueryPlugin => {
  switch (type) {
    case ComponentType.HTML:
    case ComponentType.HTML_TABLE_OF_CONTENTS:
      return JQueryPlugin.HTML;
    case ComponentType.SPACER:
      return JQueryPlugin.SPACER;
    case ComponentType.HORIZONTAL_RULE:
      return JQueryPlugin.HORIZONTAL_RULE;
    case ComponentType.ASSET_SELECTOR:
    case ComponentType.CLASSIC_VIZ: {
      /* CLASSIC_VIZ is no longer a supported type. Existing components are rendered as
       * ASSET_SELECTOR so users are prompted to replace them with a supported visualization.
       * Remove this case once all classic viz components have been migrated. */
      return JQueryPlugin.ASSET_SELECTOR;
    }
    case ComponentType.AUTHOR:
      return JQueryPlugin.AUTHOR;
    case ComponentType.IMAGE:
      return JQueryPlugin.IMAGE;
    case ComponentType.HERO:
      return JQueryPlugin.HERO;
    case ComponentType.GLOBAL_FILTER:
      return JQueryPlugin.GLOBAL_FILTER;
    case ComponentType.STORY_TILE:
    case ComponentType.STORY_WIDGET:
      return JQueryPlugin.STORY_TILE;
    case ComponentType.MEASURE_CARD:
    case ComponentType.MEASURE_CHART:
      return JQueryPlugin.MEASURE;
    case ComponentType.YOUTUBE:
      return JQueryPlugin.YOUTUBE;
    case ComponentType.MAP:
    case ComponentType.FEATURE_MAP:
    case ComponentType.REGION_MAP:
    case ComponentType.CHOROPLETH_MAP: // legacy
      return JQueryPlugin.MAP;
    case ComponentType.CALENDAR:
    case ComponentType.COLUMN_CHART:
    case ComponentType.SCATTER_CHART:
    case ComponentType.COMBO_CHART:
    case ComponentType.BAR_CHART:
    case ComponentType.PIE_CHART:
    case ComponentType.TIMELINE_CHART:
    case ComponentType.HISTOGRAM:
      return JQueryPlugin.INSITU;
    case ComponentType.TABLE:
    case ComponentType.AG_TABLE:
      return JQueryPlugin.TABLE;
    case ComponentType.VIZ_CANVAS:
      return JQueryPlugin.VIZ_CANVAS;
    case ComponentType.EMBEDDED_HTML:
      return JQueryPlugin.EMBEDDED_HTML;
    default:
      throw new Error(`No component renderer found for type: ${type}`);
  }
};
